// Organization Context for dashboard view state, filters, and bulk selection
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const OrganizationContext = createContext(null);

// Default filter state
const DEFAULT_FILTERS = {
    status: 'active', // 'active' | 'archived' | 'deleted' | 'favorites'
    dateRange: null,  // { start: Date, end: Date } | null
    searchQuery: '',
    sortBy: 'updatedAt', // 'updatedAt' | 'createdAt' | 'title'
    sortOrder: 'desc' // 'asc' | 'desc'
};

// Local storage key for view mode persistence
const VIEW_MODE_STORAGE_KEY = 'pageNotesViewMode';

export function OrganizationProvider({ children }) {
    // View State (persisted to localStorage)
    const [viewMode, setViewModeState] = useState(() => {
        try {
            return localStorage.getItem(VIEW_MODE_STORAGE_KEY) || 'grid';
        } catch {
            return 'grid';
        }
    });

    // Filter State
    const [filters, setFilters] = useState(DEFAULT_FILTERS);

    // Bulk Selection State
    const [selectedNoteIds, setSelectedNoteIds] = useState(new Set());
    const [bulkActionMode, setBulkActionMode] = useState(false);

    // Persist viewMode to localStorage
    useEffect(() => {
        try {
            localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
        } catch (err) {
            console.error('Failed to save view mode to localStorage:', err);
        }
    }, [viewMode]);

    // Toggle between grid and list view
    const toggleViewMode = useCallback(() => {
        setViewModeState(prev => prev === 'grid' ? 'list' : 'grid');
    }, []);

    // Set specific view mode
    const setViewMode = useCallback((mode) => {
        if (mode === 'grid' || mode === 'list') {
            setViewModeState(mode);
        }
    }, []);

    // Update filter state (partial updates)
    const updateFilters = useCallback((updates) => {
        setFilters(prev => ({
            ...prev,
            ...updates
        }));
    }, []);

    // Reset filters to defaults
    const resetFilters = useCallback(() => {
        setFilters(DEFAULT_FILTERS);
    }, []);

    // Set status filter
    const setStatusFilter = useCallback((status) => {
        setFilters(prev => ({
            ...prev,
            status
        }));
    }, []);

    // Set search query
    const setSearchQuery = useCallback((query) => {
        setFilters(prev => ({
            ...prev,
            searchQuery: query
        }));
    }, []);

    // Set sort options
    const setSortOptions = useCallback((sortBy, sortOrder) => {
        setFilters(prev => ({
            ...prev,
            sortBy: sortBy || prev.sortBy,
            sortOrder: sortOrder || prev.sortOrder
        }));
    }, []);

    // Set date range filter
    const setDateRange = useCallback((dateRange) => {
        setFilters(prev => ({
            ...prev,
            dateRange
        }));
    }, []);

    // Toggle note in selection
    const toggleNoteSelection = useCallback((noteId) => {
        setSelectedNoteIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(noteId)) {
                newSet.delete(noteId);
            } else {
                newSet.add(noteId);
            }
            
            // Auto-enable bulk mode when selecting
            if (newSet.size > 0 && !bulkActionMode) {
                setBulkActionMode(true);
            }
            
            return newSet;
        });
    }, [bulkActionMode]);

    // Select all provided notes
    const selectAllNotes = useCallback((noteIds) => {
        setSelectedNoteIds(new Set(noteIds));
        if (noteIds.length > 0) {
            setBulkActionMode(true);
        }
    }, []);

    // Clear all selections
    const clearSelection = useCallback(() => {
        setSelectedNoteIds(new Set());
    }, []);

    // Exit bulk action mode and clear selection
    const exitBulkMode = useCallback(() => {
        setBulkActionMode(false);
        setSelectedNoteIds(new Set());
    }, []);

    // Enter bulk action mode
    const enterBulkMode = useCallback(() => {
        setBulkActionMode(true);
    }, []);

    // Check if a note is selected
    const isNoteSelected = useCallback((noteId) => {
        return selectedNoteIds.has(noteId);
    }, [selectedNoteIds]);

    // Get count of selected notes
    const selectedCount = useMemo(() => {
        return selectedNoteIds.size;
    }, [selectedNoteIds]);

    // Get selected note IDs as array
    const selectedNoteIdsArray = useMemo(() => {
        return Array.from(selectedNoteIds);
    }, [selectedNoteIds]);

    // Check if any filters are active (non-default)
    const hasActiveFilters = useMemo(() => {
        return (
            filters.status !== DEFAULT_FILTERS.status ||
            filters.searchQuery !== DEFAULT_FILTERS.searchQuery ||
            filters.dateRange !== DEFAULT_FILTERS.dateRange ||
            filters.sortBy !== DEFAULT_FILTERS.sortBy ||
            filters.sortOrder !== DEFAULT_FILTERS.sortOrder
        );
    }, [filters]);

    const value = {
        // View state
        viewMode,
        toggleViewMode,
        setViewMode,
        
        // Filter state
        filters,
        updateFilters,
        resetFilters,
        setStatusFilter,
        setSearchQuery,
        setSortOptions,
        setDateRange,
        hasActiveFilters,
        
        // Bulk selection state
        selectedNoteIds: selectedNoteIdsArray,
        selectedCount,
        bulkActionMode,
        toggleNoteSelection,
        selectAllNotes,
        clearSelection,
        exitBulkMode,
        enterBulkMode,
        isNoteSelected
    };

    return (
        <OrganizationContext.Provider value={value}>
            {children}
        </OrganizationContext.Provider>
    );
}

export function useOrganization() {
    const context = useContext(OrganizationContext);
    if (!context) {
        throw new Error('useOrganization must be used within an OrganizationProvider');
    }
    return context;
}